/*
 * Copyright 2026 Sony Corporation
 */

import { z } from 'zod';

import { MAX_THUMBNAIL_CHARS, SCENE_CONTENT_TYPE } from './scenes.js';

/**
 * The user's own assets: uploads, and later the outputs of a reconstruction
 * (3d-reconstruction.md §7, devplan §5.7).
 *
 * An asset belongs to one user, not to a world. It is reached through its row
 * alone: the service hands back a presigned GET for a ready asset, or nothing,
 * and never a URL the browser could have built itself.
 */

export const AssetIdSchema = z.uuid();

/** What the importer picks its loader by, so it never guesses from a URL. */
export const ASSET_KINDS = ['model', 'video', 'splat'] as const;
export const AssetKindSchema = z.enum(ASSET_KINDS);
export type AssetKind = z.infer<typeof AssetKindSchema>;

const MB = 1024 * 1024;

/** Devplan §5.7's cap for anything that ends up inside a scene. */
export const MAX_ASSET_BYTES = 50 * MB;

/** A video is only ever reconstructed from, never placed, so it may be larger. */
export const MAX_VIDEO_BYTES = 1024 * MB;

/**
 * What may be uploaded, and what each is.
 *
 * The content type is signed into the upload and re-checked at publish, so it
 * is the one thing about a file this service can trust without opening it.
 */
export const UPLOAD_TYPES = {
  'model/gltf-binary': { kind: 'model', max_bytes: MAX_ASSET_BYTES },
  [SCENE_CONTENT_TYPE]: { kind: 'model', max_bytes: MAX_ASSET_BYTES },
  'application/x-spz': { kind: 'splat', max_bytes: MAX_ASSET_BYTES },
  'video/mp4': { kind: 'video', max_bytes: MAX_VIDEO_BYTES },
  'video/quicktime': { kind: 'video', max_bytes: MAX_VIDEO_BYTES },
} as const satisfies Record<string, { kind: AssetKind; max_bytes: number }>;

export type UploadType = keyof typeof UPLOAD_TYPES;
export const UploadTypeSchema = z.enum(Object.keys(UPLOAD_TYPES) as [UploadType, ...UploadType[]]);

/**
 * An upload goes `uploading → ready`. A reconstruction's splat goes
 * `queued → preparing → training → finalizing → ready`, or ends `failed`
 * (3d-reconstruction.md §7.1). Deleting a running output cancels it.
 */
export const ASSET_STATUSES = [
  'uploading',
  'queued',
  'preparing',
  'training',
  'finalizing',
  'ready',
  'failed',
] as const;
export const AssetStatusSchema = z.enum(ASSET_STATUSES);
export type AssetStatus = z.infer<typeof AssetStatusSchema>;

/** Still owned by a running execution. */
export const RUNNING_STATUSES: readonly AssetStatus[] = ['queued', 'preparing', 'training', 'finalizing'];

/**
 * Where a running stage's training job is, from SageMaker's own status.
 *
 * `waiting`: no job yet, because another reconstruction holds the instance
 * quota. `starting`: SageMaker is finding an instance, which took 1 h 47 min
 * once in R4. `running`: the job has its instance.
 */
export const JOB_PHASES = ['waiting', 'starting', 'running'] as const;
export const JobSchema = z.object({ phase: z.enum(JOB_PHASES), since: z.string() });
export type Job = z.infer<typeof JobSchema>;

export const AssetNameSchema = z.string().trim().min(1).max(64);

/**
 * Metres per unit of the asset's own file, set by the editor's known-distance
 * tool (3d-reconstruction.md §6.4). A plain video has no metric source, so a
 * reconstruction's units are whatever COLMAP chose until someone measures.
 */
export const AssetScaleSchema = z.number().min(1e-4).max(1e4);

export const AssetSchema = z.object({
  asset_id: AssetIdSchema,
  owner_id: z.string(),
  name: AssetNameSchema,
  kind: AssetKindSchema,
  content_type: UploadTypeSchema,
  /** As declared at creation until publish, then as S3 reports it. */
  bytes: z.number().int().nonnegative(),
  status: AssetStatusSchema,
  /** Signed per response, and only when a thumbnail was stored. */
  thumbnail_url: z.string().optional(),
  /** A reconstruction's outputs: the video they came from, and the run they share (§6.4). */
  source_asset_id: AssetIdSchema.optional(),
  reconstruction_id: AssetIdSchema.optional(),
  /** Why a reconstruction failed, in words a user can act on. */
  failure: z.string().optional(),
  /** The output's section of the pipeline's `report.json` (§6.3). */
  report: z.record(z.string(), z.unknown()).optional(),
  /** Absent means 1: the file's units are metres. */
  scale: AssetScaleSchema.optional(),
  /** Only in a listing, and only while a stage's job is the thing being waited on. */
  job: JobSchema.optional(),
  created_at: z.string(),
  updated_at: z.string(),
});
export type Asset = z.infer<typeof AssetSchema>;

export const PathParametersAssetSchema = z.object({ asset_id: AssetIdSchema });

export const CreateAssetRequestSchema = z
  .object({ name: AssetNameSchema, content_type: UploadTypeSchema, bytes: z.number().int().positive() })
  .strict()
  .refine((body) => body.bytes <= UPLOAD_TYPES[body.content_type].max_bytes, {
    message: 'is larger than this kind of asset may be',
    path: ['bytes'],
  });
export type CreateAssetRequest = z.infer<typeof CreateAssetRequestSchema>;

export const CreateAssetResponseSchema = z.object({
  asset: AssetSchema,
  /** Signed for exactly this content type and length; anything else is refused by S3. */
  upload_url: z.string(),
  expires_at: z.string(),
});
export type CreateAssetResponse = z.infer<typeof CreateAssetResponseSchema>;

export const PublishAssetRequestSchema = z
  .object({
    /** Base64 JPEG. Optional: an upload must not fail for want of a picture. */
    thumbnail: z.base64().max(MAX_THUMBNAIL_CHARS).optional(),
  })
  .strict();
export type PublishAssetRequest = z.infer<typeof PublishAssetRequestSchema>;

export const UpdateAssetRequestSchema = z
  .object({ name: AssetNameSchema.optional(), scale: AssetScaleSchema.optional() })
  .strict()
  .refine((body) => body.name !== undefined || body.scale !== undefined, { message: 'changes nothing' });
export type UpdateAssetRequest = z.infer<typeof UpdateAssetRequestSchema>;

export const AssetResponseSchema = z.object({ asset: AssetSchema });
export const AssetsResponseSchema = z.object({ assets: z.array(AssetSchema) });

export const AssetLoadResponseSchema = z.object({
  asset: AssetSchema,
  /** Present only once the asset is ready. */
  url: z.string().optional(),
  expires_at: z.string().optional(),
});
export type AssetLoadResponse = z.infer<typeof AssetLoadResponseSchema>;

/** The splat a reconstruction will produce, created `queued`. */
export const ReconstructResponseSchema = z.object({ splat: AssetSchema });
export type ReconstructResponse = z.infer<typeof ReconstructResponseSchema>;

/**
 * What the state machine is started with, and hands each stage.
 *
 * Keys, not URLs, and every one derived by the service from ids it created.
 */
export const ReconstructionInputSchema = z.object({
  reconstruction_id: AssetIdSchema,
  video_asset_id: AssetIdSchema,
  splat_asset_id: AssetIdSchema,
});
export type ReconstructionInput = z.infer<typeof ReconstructionInputSchema>;

/**
 * The asset library's second source (devplan §5.5).
 *
 * Poly Haven is reached straight from the browser; Sketchfab is not, and the
 * difference is not CORS — their search does send our origin back. It is that
 * the licence filter has to be ours to set. A browser that composes its own
 * query can ask for `by-nc-nd`, and §5.6's save step bakes whatever it gets
 * into a published `.babylon`. Enforced here, the set of acceptable licences
 * is a property of the deployment rather than a habit of one component.
 */

/** Sketchfab's own id: 32 lower-case hex, and checked before it reaches a URL. */
export const SketchfabUidSchema = z.string().regex(/^[0-9a-f]{32}$/, 'must be a Sketchfab uid');

/**
 * The licences this service will search.
 *
 * **PoC setting.** §5.6's rule is that a source must be embeddable in a saved
 * `.babylon` and redistributable with it, which only CC0 satisfies outright:
 * the CC BY family needs a credit the baked file cannot carry, `by-nc-sa` also
 * forbids commercial use and forces the same licence on anything it is
 * combined with, and the Standard licences do not permit redistribution at
 * all. Kept as a list, and enforced here, so narrowing it back to `['cc0']`
 * before this is anything but a demonstration is a one-line change in one
 * place.
 *
 * `st` yields nothing while `downloadable=true` stands — Standard-licence
 * models are store purchases — but it is listed because it was asked for, and
 * because its absence would otherwise look like an oversight.
 */
export const SKETCHFAB_LICENSES = ['cc0', 'by', 'by-nc-sa', 'st', 'free-st'] as const;

/** Sketchfab caps a page at 24 regardless of what is asked for. */
export const SKETCHFAB_PAGE = 24;

export const SketchfabAssetSchema = z.object({
  uid: SketchfabUidSchema,
  name: z.string(),
  /** The author's username. CC0 obliges nobody, but the other licences here do (devplan §5.8). */
  author: z.string(),
  /** Their label for it, shown on the tile: what may be done with the model is not uniform any more. */
  license: z.string().optional(),
  thumbnail_url: z.string(),
  /** Their page for the model, which is where a credit should point. */
  viewer_url: z.string(),
  /** What an import would cost, so the grid can say so before anyone waits for it. */
  bytes: z.number().int().nonnegative().optional(),
  face_count: z.number().int().nonnegative().optional(),
});
export type SketchfabAsset = z.infer<typeof SketchfabAssetSchema>;

export const SketchfabSearchResponseSchema = z.object({
  assets: z.array(SketchfabAssetSchema),
  /** Opaque; hand it back to page on. Absent at the end of the results. */
  cursor: z.string().optional(),
});
export type SketchfabSearchResponse = z.infer<typeof SketchfabSearchResponseSchema>;

export const SketchfabSearchQuerySchema = z.object({
  q: z.string().max(120).optional(),
  cursor: z.string().max(200).optional(),
});
export type SketchfabSearchQuery = z.infer<typeof SketchfabSearchQuerySchema>;
